const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const normalizeText = (raw: string): string =>
  raw
    .replace(/\u202f/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/^Shape\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const isMainHeading = (line: string): boolean =>
  /^\d+\.\s+[A-Za-z]/.test(line) ||
  /^introduction$/i.test(line) ||
  /^terms and conditions/i.test(line);

const isSubHeading = (line: string): boolean =>
  /^\d+\.\d+\.?\s+/.test(line);

const isLetterHeading = (line: string): boolean =>
  /^[A-Z]\.\s+[A-Za-z]/.test(line);

const isListBlock = (lines: string[]): boolean =>
  lines.length > 1 &&
  lines.every(
    (line) =>
      line.startsWith("- ") ||
      line.startsWith("• ") ||
      /^[•\-]\s/.test(line) ||
      /^[A-Za-z][A-Za-z\s/&()]+:\s/.test(line) ||
      /^Right to [A-Za-z\s]+:/.test(line)
  );

const formatInline = (text: string): string => {
  let html = escapeHtml(text);

  html = html.replace(
    /^([A-Za-z][A-Za-z\s/&()]+):\s*/,
    "<strong>$1:</strong> "
  );

  html = html.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '<a href="mailto:$1">$1</a>'
  );

  return html;
};

const blockToHtml = (block: string): string => {
  const lines = block
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return "";

  if (lines.length === 1) {
    const line = lines[0];

    if (/^introduction$/i.test(line)) {
      return `<h2>${escapeHtml(line)}</h2>`;
    }
    if (isMainHeading(line)) {
      return `<h2>${escapeHtml(line)}</h2>`;
    }
    if (isSubHeading(line)) {
      return `<h3>${escapeHtml(line)}</h3>`;
    }
    if (isLetterHeading(line)) {
      return `<h4>${escapeHtml(line)}</h4>`;
    }
    return `<p>${formatInline(line)}</p>`;
  }

  if (isListBlock(lines)) {
    const items = lines
      .map((line) => line.replace(/^[-•]\s*/, ""))
      .map((line) => `<li>${formatInline(line)}</li>`)
      .join("");
    return `<ul>${items}</ul>`;
  }

  const [first, ...rest] = lines;

  if (isLetterHeading(first) || isSubHeading(first)) {
    const headingTag = isSubHeading(first) ? "h3" : "h4";
    const body = rest
      .map((line) => `<p>${formatInline(line)}</p>`)
      .join("");
    return `<${headingTag}>${escapeHtml(first)}</${headingTag}>${body}`;
  }

  if (isMainHeading(first)) {
    const body = rest
      .map((line) => `<p>${formatInline(line)}</p>`)
      .join("");
    return `<h2>${escapeHtml(first)}</h2>${body}`;
  }

  return lines.map((line) => `<p>${formatInline(line)}</p>`).join("");
};

export const formatPolicyContent = (
  raw: string,
  options?: { stopAtTerms?: boolean }
): string => {
  let text = normalizeText(raw);

  if (options?.stopAtTerms !== false) {
    const termsMatch = text.search(/\nTERMS AND CONDITIONS/i);
    if (termsMatch > 0) {
      text = text.slice(0, termsMatch).trim();
    }
  }

  const blocks = text.split(/\n\n+/).map((b) => b.trim()).filter(Boolean);

  return blocks.map(blockToHtml).join("");
};
